import { useState, useRef } from 'react';
import { X, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { useAppStore } from '../../stores/wpAppStore';
import * as wpApi from '../../services/wordpressApi';

interface CreatePostModalProps {
  open: boolean;
  onClose: () => void;
}

const CATEGORIES = ['Snooker', 'Pool9', '3C', 'Libre', 'Tournament'];

const styles = {
  backdrop: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    zIndex: 120,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    animation: 'fadeIn 0.2s ease',
  },
  modal: {
    background: '#18181b',
    border: '1px solid #27272a',
    borderRadius: '24px',
    padding: '20px',
    maxWidth: '32rem',
    width: '100%',
    maxHeight: '85vh',
    overflowY: 'auto' as const,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
    animation: 'slideUp 0.25s ease',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#fafafa',
    margin: 0,
  },
  closeBtn: {
    padding: '4px',
    color: '#a1a1aa',
    background: 'none',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#fafafa',
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    background: '#27272a',
    border: '1px solid #27272a',
    borderRadius: '12px',
    color: '#fafafa',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  textarea: {
    width: '100%',
    padding: '12px 16px',
    background: '#27272a',
    border: '1px solid #27272a',
    borderRadius: '12px',
    color: '#fafafa',
    fontSize: '14px',
    outline: 'none',
    resize: 'none' as const,
    minHeight: '120px',
    transition: 'border-color 0.2s',
    fontFamily: 'inherit',
  },
  hint: {
    fontSize: '12px',
    color: '#71717a',
  },
  imagePreview: {
    position: 'relative' as const,
    borderRadius: '12px',
    overflow: 'hidden' as const,
    background: '#27272a',
  },
  image: {
    width: '100%',
    maxHeight: '240px',
    objectFit: 'cover' as const,
    display: 'block',
  },
  uploadOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '2px solid #3b82f6',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '8px',
  },
  removeBtn: {
    position: 'absolute' as const,
    top: '8px',
    right: '8px',
    width: '32px',
    height: '32px',
    background: 'rgba(0, 0, 0, 0.7)',
    border: 'none',
    borderRadius: '50%',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
  },
  uploadZone: {
    border: '2px dashed #27272a',
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  uploadIcon: {
    color: '#71717a',
    marginBottom: '8px',
  },
  uploadText: {
    fontSize: '14px',
    color: '#a1a1aa',
    fontWeight: 500,
  },
  urlInput: {
    width: '100%',
    padding: '12px 16px',
    background: '#27272a',
    border: '1px solid #27272a',
    borderRadius: '12px',
    color: '#fafafa',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    paddingTop: '16px',
    borderTop: '1px solid #27272a',
  },
  cancelBtn: {
    flex: 1,
    padding: '12px 20px',
    background: '#27272a',
    color: '#a1a1aa',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  submitBtn: {
    flex: 1,
    padding: '12px 20px',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255, 255, 255, 0.35)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginRight: '8px',
  },
  errorOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.65)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    zIndex: 140,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  },
  errorModal: {
    background: '#18181b',
    border: '1px solid #27272a',
    borderRadius: '20px',
    padding: '24px',
    maxWidth: '360px',
    width: '100%',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    textAlign: 'center' as const,
  },
  errorIcon: {
    fontSize: '40px',
    marginBottom: '8px',
  },
  errorTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#fafafa',
    margin: '0 0 8px 0',
  },
  errorText: {
    fontSize: '14px',
    color: '#a1a1aa',
    margin: '0 0 20px 0',
    wordBreak: 'break-word' as const,
  },
  errorBtn: {
    width: '100%',
    padding: '12px',
    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

export default function CreatePostModal({ open, onClose }: CreatePostModalProps) {
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('Snooker');
  const [imageUrl, setImageUrl] = useState('');
  const [imageId, setImageId] = useState<number | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadFeed = useAppStore((s) => s.loadFeed);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const uploaded = await wpApi.uploadImage(file);
      setImageUrl(uploaded.url);
      setImageId(uploaded.id);
    } catch (error) {
      console.error('Upload failed:', error);
      setImagePreview(null);
      setImageId(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    if (!content.trim()) {
      toast.error('Vui lòng nhập nội dung bài viết.');
      return;
    }

    setSubmitting(true);
    try {
      const hashtagRegex = /#[\p{L}0-9_-]+/gu;
      const hashtags = content.match(hashtagRegex) || [];

      await wpApi.createPost({
        content: content.trim(),
        category,
        hashtags,
        image_url: imageUrl || undefined,
        image_id: imageId || undefined,
      });

      setContent('');
      setImageUrl('');
      setImageId(null);
      setImagePreview(null);
      setCategory('Snooker');
      onClose();
      loadFeed();
      toast.success('Đã đăng bài');
    } catch (error) {
      console.error('Failed to create post:', error);
      toast.error(
        (error instanceof Error ? error.message : String(error)) ||
          'Không thể đăng bài. Vui lòng thử lại.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setContent('');
    setImageUrl('');
    setImageId(null);
    setImagePreview(null);
    setCategory('Snooker');
    onClose();
  };

  return open ? (
    <div style={styles.backdrop} onClick={() => !submitting && handleClose()}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
            <h2 style={styles.title}>Tạo bài viết mới</h2>
            <button
              style={styles.closeBtn}
              onClick={handleClose}
              disabled={submitting}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#3f3f46';
                e.currentTarget.style.color = '#fafafa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.color = '#a1a1aa';
              }}
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} style={styles.form} noValidate>
            {/* Category */}
            <div style={styles.field}>
              <label style={styles.label}>Danh mục</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={styles.select}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#27272a'}
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Content */}
            <div style={styles.field}>
              <label style={styles.label}>Nội dung</label>
              <textarea
                style={styles.textarea}
                placeholder="Chia sẻ khoảnh khắc của bạn..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={4}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#27272a'}
              />
              <p style={styles.hint}>Sử dụng #hashtag để thêm tags</p>
            </div>

            {/* Image */}
            <div style={styles.field}>
              <label style={styles.label}>Hình ảnh (tùy chọn)</label>
              
              {imagePreview ? (
                <div style={styles.imagePreview}>
                  <img src={imagePreview} alt="Preview" style={styles.image} />
                  {uploading && (
                    <div style={styles.uploadOverlay}>
                      <div style={styles.spinner} />
                      <span style={{ fontSize: '14px' }}>Đang tải lên...</span>
                    </div>
                  )}
                  <button
                    type="button"
                    style={styles.removeBtn}
                    onClick={() => { setImagePreview(null); setImageUrl(''); setImageId(null); }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#ef4444'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)'}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div 
                  style={styles.uploadZone}
                  onClick={() => fileInputRef.current?.click()}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#27272a';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Camera size={40} style={styles.uploadIcon} />
                  </div>
                  <p style={styles.uploadText}>Chọn ảnh từ thiết bị</p>
                </div>
              )}
              
              <input
                type="url"
                style={styles.urlInput}
                placeholder="Hoặc dán link ảnh..."
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#27272a'}
              />
            </div>

            {/* Actions */}
            <div style={styles.actions}>
              <button
                type="button"
                style={styles.cancelBtn}
                onClick={handleClose}
                disabled={submitting}
                onMouseEnter={(e) => e.currentTarget.style.background = '#3f3f46'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#27272a'}
              >
                Hủy
              </button>
              <button
                type="submit"
                style={{
                  ...styles.submitBtn,
                  opacity: (uploading || submitting) ? 0.5 : 1,
                  cursor: (uploading || submitting) ? 'not-allowed' : 'pointer',
                  transform: (uploading || submitting) ? 'none' : undefined,
                }}
                disabled={uploading || submitting}
                onMouseEnter={(e) => {
                  if (!uploading && !submitting) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 0 20px rgba(59, 130, 246, 0.25)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                }}
              >
                {submitting ? (
                  <>
                    <div style={styles.btnSpinner} />
                    Đang đăng bài...
                  </>
                ) : 'Đăng bài'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  : null;
}
